/**
 * ManageDashboard — Manage Overview v2.
 *
 * Source: design_handoff_tank/tank-redesign/screens-rest.jsx +
 * manage-redesign-v2.md brief.
 *
 * Layout:
 *   1. Page header (title + subtitle)
 *   2. 4-up clickable KPI row (Persons / Unlinked / Teams / Training).
 *      Each tile filters into its parent list when clicked.
 *   3. Model card + Activity feed (two-column).
 *   4. Quick-access Roster (3 persons) and Company (5 section cards) tiles.
 */

import { useEffect, useState } from 'react';
import {
  fetchTrainingStats,
  fetchTeams,
  fetchPersons,
  fetchUsers,
  fetchFtpSources,
  fetchFtpTargets,
  fetchMatches,
  fetchPhotoStats,
  fetchManageActivity,
} from '../../../../api';
import Spinner from '../../../../shared/components/ui/Spinner';
import { Btn, Mono, Stat } from '../../../../shared/components/redesign';
import styles from './ManageDashboard.module.css';

// Compact time formatter for the activity feed. Mirrors the brief's
// "12:14 / 11:58 / yesterday" pacing — same-day → HH:MM, prior days →
// relative phrase. Inline because Tank doesn't have a shared util yet.
function formatActivityTime(iso) {
  const t = new Date(iso);
  if (!Number.isFinite(t.getTime())) return '—';
  const now = new Date();
  const sameDay =
    t.getFullYear() === now.getFullYear() &&
    t.getMonth() === now.getMonth() &&
    t.getDate() === now.getDate();
  if (sameDay) {
    return t.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
    });
  }
  const diffDays = Math.floor((now.getTime() - t.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays === 1) return 'yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  return t.toLocaleDateString(undefined, { day: '2-digit', month: 'short' });
}

/**
 * Clickable KPI tile — filters into its parent list when clicked.
 * Active state shows a status-color border.
 */
function KpiTile({ label, value, kind = 'pending', onClick, hint }) {
  return (
    <button
      type="button"
      className={`${styles.kpiTile} ${styles[`kind-${kind}`]}`}
      onClick={onClick}
    >
      <Mono className={styles.kpiLabel}>{label}</Mono>
      <span className={styles.kpiValue}>{value ?? '—'}</span>
      {hint && <Mono className={styles.kpiHint}>{hint}</Mono>}
    </button>
  );
}

/**
 * Model card — shows labeled vs unlabeled face progress + Train / Re-detect.
 */
function ModelCard({
  labeled,
  unlabeled,
  total,
  pendingTraining,
  onTrain,
  onRedetect,
}) {
  const labeledPct = total > 0 ? Math.round((labeled / total) * 100) : 0;
  const pendingPct = total > 0 ? Math.round((pendingTraining / total) * 100) : 0;
  return (
    <div className={styles.modelCard}>
      <div className={styles.modelHeader}>
        <Mono className={styles.sectionEyebrow}>Model</Mono>
        <Mono className={styles.modelStats}>
          {labeled.toLocaleString()} / {total.toLocaleString()} labeled
        </Mono>
      </div>
      <div className={styles.progressTrack}>
        <div
          className={`${styles.progressFill} ${styles['fill-done']}`}
          style={{ width: `${labeledPct}%` }}
          title={`${labeled.toLocaleString()} labeled`}
        />
        <div
          className={`${styles.progressFill} ${styles['fill-attention']}`}
          style={{ width: `${pendingPct}%` }}
          title={`${pendingTraining.toLocaleString()} in queue`}
        />
      </div>
      <div className={styles.progressLegend}>
        <span className={styles.legendItem}>
          <span
            className={`${styles.legendDot} ${styles['fill-done']}`}
            aria-hidden="true"
          />
          <Mono>Trained {labeledPct}%</Mono>
        </span>
        <span className={styles.legendItem}>
          <span
            className={`${styles.legendDot} ${styles['fill-attention']}`}
            aria-hidden="true"
          />
          <Mono>In queue {pendingPct}%</Mono>
        </span>
        <span className={styles.legendItem}>
          <span className={styles.legendDot} aria-hidden="true" />
          <Mono>Unlabeled {Math.max(0, 100 - labeledPct - pendingPct)}%</Mono>
        </span>
      </div>
      <div className={styles.modelActions}>
        <Btn kind="primary" size="sm" onClick={onTrain}>
          Train now
        </Btn>
        <Btn kind="ghost" size="sm" onClick={onRedetect}>
          Re-detect faces
        </Btn>
      </div>
    </div>
  );
}

/**
 * Activity feed — recent events + Next-match footer.
 *
 * TODO(redesign): no audit-log API exists yet on imagebase/operator. For now
 * we synthesize from training stats + next match. When the audit-log API
 * lands, swap in real events.
 */
function ActivityFeed({ events, nextMatch, onOpenMatch }) {
  const hasEvents = events && events.length > 0;
  return (
    <div className={styles.activityCard}>
      <div className={styles.activityHeader}>
        <Mono className={styles.sectionEyebrow}>Activity</Mono>
      </div>
      {hasEvents ? (
        <ul className={styles.activityList}>
          {events.slice(0, 4).map((e, i) => (
            <li key={i} className={styles.activityRow}>
              <Mono className={styles.activityTime}>{e.time}</Mono>
              <span className={styles.activityText}>{e.text}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className={styles.activityEmpty}>
          No recent activity in the last 24 h.
        </p>
      )}
      {nextMatch && (
        <button
          type="button"
          className={styles.nextMatchFooter}
          onClick={() => onOpenMatch?.(nextMatch)}
        >
          <Mono className={styles.sectionEyebrow}>Next match</Mono>
          <span className={styles.nextMatchTitle}>
            {nextMatch.home_team_name && nextMatch.away_team_name
              ? `${nextMatch.home_team_name} vs ${nextMatch.away_team_name}`
              : nextMatch.name || 'Untitled match'}
          </span>
          <Mono className={styles.nextMatchTime}>
            {new Date(nextMatch.start_time).toLocaleString(undefined, {
              day: '2-digit',
              month: 'short',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </Mono>
        </button>
      )}
    </div>
  );
}

/**
 * Quick-access Roster preview — top N persons by face_count.
 */
function RosterPreview({ persons, onNavigate }) {
  const top = (persons || []).slice(0, 3);
  return (
    <div className={styles.previewCard}>
      <button
        type="button"
        className={styles.previewHeader}
        onClick={() => onNavigate('/manage/persons')}
      >
        <Mono className={styles.sectionEyebrow}>Roster · top 3</Mono>
        <span className={styles.previewLink}>Open Persons →</span>
      </button>
      {top.length === 0 ? (
        <p className={styles.previewEmpty}>No persons yet.</p>
      ) : (
        <ul className={styles.previewList}>
          {top.map((p) => {
            const initials =
              `${p.first_name?.[0] || ''}${p.last_name?.[0] || ''}`.toUpperCase() ||
              p.display_name?.slice(0, 2).toUpperCase() ||
              '·';
            return (
              <li key={p.id} className={styles.previewRow}>
                <span className={styles.previewAvatar}>{initials}</span>
                <span className={styles.previewName}>
                  {p.display_name ||
                    `${p.first_name || ''} ${p.last_name || ''}`.trim() ||
                    `Person ${p.id}`}
                </span>
                {p.team_name && (
                  <Mono className={styles.previewMeta}>{p.team_name}</Mono>
                )}
                <Mono className={styles.previewMeta}>
                  {p.face_count || 0} faces
                </Mono>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/**
 * Quick-access Company preview — 5 section tiles with count + drill-in.
 */
function CompanyPreview({ counts, onNavigate, canManageCompany }) {
  if (!canManageCompany) return null;
  const sections = [
    {
      key: 'users',
      label: 'Users',
      count: counts.users,
      hash: '#company/users',
    },
    {
      key: 'ftp-sources',
      label: 'FTP sources',
      count: counts.ftpSources,
      hash: '#company/ftp-sources',
    },
    {
      key: 'ftp-targets',
      label: 'FTP targets',
      count: counts.ftpTargets,
      hash: '#company/ftp-targets',
    },
    {
      key: 'profiles',
      label: 'Profiles',
      count: counts.profiles ?? '—',
      hash: '#company/profiles',
    },
    {
      key: 'caption',
      label: 'Caption builder',
      count: counts.captions ?? '—',
      hash: '#company/caption',
    },
  ];
  return (
    <div className={styles.previewCard}>
      <button
        type="button"
        className={styles.previewHeader}
        onClick={() => onNavigate('/manage/company')}
      >
        <Mono className={styles.sectionEyebrow}>Company · 5 sections</Mono>
        <span className={styles.previewLink}>Open Company →</span>
      </button>
      <ul className={styles.companyGrid}>
        {sections.map((s) => (
          <li key={s.key}>
            <button
              type="button"
              className={styles.companyTile}
              onClick={() => onNavigate(`/manage/company${s.hash}`)}
            >
              <Mono className={styles.companyLabel}>{s.label}</Mono>
              <span className={styles.companyCount}>{s.count ?? '—'}</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function ManageDashboard({
  onNavigate,
  canManagePersons = false,
  canManageCompany = false,
}) {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    training: null,
    teams: null,
    persons: null,
    unlinked: null,
    users: null,
    ftpSources: null,
    ftpTargets: null,
    matches: null,
    photos: null,
    topPersons: null,
    nextMatch: null,
    activity: null,
  });

  useEffect(() => {
    const loadStats = async () => {
      setLoading(true);
      try {
        const results = await Promise.allSettled([
          fetchTrainingStats(),
          canManagePersons ? fetchTeams({ limit: 1 }) : Promise.resolve(null),
          canManagePersons ? fetchPersons({ limit: 1 }) : Promise.resolve(null),
          canManagePersons
            ? fetchPersons({ team: 'none', limit: 1 })
            : Promise.resolve(null),
          canManageCompany ? fetchUsers({ limit: 1 }) : Promise.resolve(null),
          canManageCompany ? fetchFtpSources({ limit: 1 }) : Promise.resolve(null),
          canManageCompany ? fetchFtpTargets({ limit: 1 }) : Promise.resolve(null),
          canManagePersons ? fetchMatches({ limit: 1 }) : Promise.resolve(null),
          fetchPhotoStats(),
          canManagePersons
            ? fetchPersons({ ordering: '-face_count', limit: 3 })
            : Promise.resolve(null),
          canManagePersons
            ? fetchMatches({
                status: 'upcoming',
                ordering: 'start_time',
                limit: 1,
              })
            : Promise.resolve(null),
          // BT-5 — recent-activity feed.
          fetchManageActivity(8),
        ]);

        setStats({
          training: results[0].status === 'fulfilled' ? results[0].value : null,
          teams: results[1].status === 'fulfilled' ? results[1].value : null,
          persons: results[2].status === 'fulfilled' ? results[2].value : null,
          unlinked: results[3].status === 'fulfilled' ? results[3].value : null,
          users: results[4].status === 'fulfilled' ? results[4].value : null,
          ftpSources: results[5].status === 'fulfilled' ? results[5].value : null,
          ftpTargets: results[6].status === 'fulfilled' ? results[6].value : null,
          matches: results[7].status === 'fulfilled' ? results[7].value : null,
          photos: results[8].status === 'fulfilled' ? results[8].value : null,
          topPersons: results[9].status === 'fulfilled' ? results[9].value : null,
          nextMatch: results[10].status === 'fulfilled' ? results[10].value : null,
          activity: results[11].status === 'fulfilled' ? results[11].value : null,
        });
      } catch (err) {
        console.error('Failed to load dashboard stats:', err);
      } finally {
        setLoading(false);
      }
    };
    loadStats();
  }, [canManagePersons, canManageCompany]);

  if (loading) {
    return (
      <div className={styles.loading}>
        <Spinner />
        <p>Loading dashboard…</p>
      </div>
    );
  }

  // Pull counts out of the heterogeneous API shapes (some endpoints return
  // {count, results}, others return raw arrays).
  const personCount = stats.persons?.count ?? stats.persons?.results?.length ?? 0;
  const teamCount = stats.teams?.count ?? stats.teams?.results?.length ?? 0;
  const unlinkedCount =
    stats.unlinked?.count ?? stats.unlinked?.results?.length ?? 0;
  const userCount = stats.users?.count ?? stats.users?.results?.length ?? 0;
  const ftpSourceCount =
    stats.ftpSources?.count ?? stats.ftpSources?.results?.length ?? 0;
  const ftpTargetCount =
    stats.ftpTargets?.count ?? stats.ftpTargets?.results?.length ?? 0;

  // Training stats
  const labeledFaces = stats.training?.faces?.labeled ?? 0;
  const unlabeledFaces = stats.training?.faces?.unlabeled ?? 0;
  const totalFaces = stats.training?.faces?.total ?? 0;
  const trainingPending = stats.training?.by_status?.ready ?? 0;

  const topPersons = stats.topPersons?.results || [];
  const nextMatch = stats.nextMatch?.results?.[0] || null;

  // BT-5 — real activity feed from operator. Each row: {kind, time, text}.
  // The endpoint synthesizes events from training sessions, person/team
  // CRUD, and recent matches; ActivityFeed slices to the first 4.
  const events = (stats.activity?.results || []).map((e) => ({
    time: formatActivityTime(e.timestamp),
    text: e.text,
  }));

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2 className={styles.title}>Manage</h2>
        <p className={styles.subtitle}>
          Roster, training queue, and company configuration.
        </p>
      </div>

      {/* 4-up clickable KPI row */}
      <div className={styles.kpiRow}>
        <KpiTile
          label="Persons"
          value={personCount.toLocaleString()}
          onClick={() => onNavigate('/manage/persons')}
        />
        <KpiTile
          label="Unlinked"
          value={unlinkedCount.toLocaleString()}
          kind={unlinkedCount > 0 ? 'attention' : 'pending'}
          onClick={() => onNavigate('/manage/unlinked')}
        />
        <KpiTile
          label="Teams"
          value={teamCount.toLocaleString()}
          onClick={() => onNavigate('/manage/teams')}
        />
        <KpiTile
          label="Training"
          value={trainingPending.toLocaleString()}
          kind={trainingPending > 0 ? 'attention' : 'done'}
          hint={trainingPending > 0 ? 'in queue' : 'caught up'}
          onClick={() => onNavigate('/manage/training')}
        />
      </div>

      {/* Two-column: Model card + Activity feed */}
      <div className={styles.modelActivityRow}>
        <ModelCard
          labeled={labeledFaces}
          unlabeled={unlabeledFaces}
          total={totalFaces}
          pendingTraining={trainingPending}
          onTrain={() => onNavigate('/manage/training')}
          onRedetect={() => onNavigate('/manage/training')}
        />
        <ActivityFeed
          events={events}
          nextMatch={nextMatch}
          onOpenMatch={(m) => onNavigate(`/matches?match=${m.id}`)}
        />
      </div>

      {/* Quick-access previews */}
      {canManagePersons && (
        <RosterPreview persons={topPersons} onNavigate={onNavigate} />
      )}

      <CompanyPreview
        counts={{
          users: userCount,
          ftpSources: ftpSourceCount,
          ftpTargets: ftpTargetCount,
          profiles: null,
          captions: null,
        }}
        onNavigate={onNavigate}
        canManageCompany={canManageCompany}
      />
    </div>
  );
}
